/**
 * The only web module that reads the browser position (docs#10).
 *
 * Every position leaves this module as the centre of its 2 km grid cell, before any caller can
 * store, send or log it. The ESLint rule in eslint.config.mjs rejects `navigator.geolocation`,
 * `.getCurrentPosition(` and `.watchPosition(` in every other file.
 */

import gridSpec from '../../../../app/Domain/Service/Location/grid-spec.json';
import { createLocationGrid } from './location-grid.js';

const grid = createLocationGrid(gridSpec);

export function hasGeolocation() {
    return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

/**
 * The Permissions API state of geolocation: 'granted', 'denied' or 'prompt'.
 * Null when the browser has no Permissions API or the query fails.
 * @returns {Promise<?string>}
 */
export async function queryGeolocationPermission() {
    try {
        if (!navigator.permissions || !navigator.permissions.query) {
            return null;
        }
        const status = await navigator.permissions.query({ name: 'geolocation' });
        return status.state;
    } catch {
        return null;
    }
}

/**
 * Request one position, snapped to the centre of its 2 km cell. Rejects with the browser's
 * GeolocationPositionError, or with an Error when the browser has no geolocation.
 * @param {{timeout?: number, maximumAge?: number}} options
 * @returns {Promise<{lat: number, lon: number, accuracy: ?number}>}
 */
export function requestSnappedPosition({ timeout = 10000, maximumAge = 300000 } = {}) {
    if (!hasGeolocation()) {
        return Promise.reject(new Error('geolocation unavailable'));
    }

    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                // The callback runs outside the executor, so a snap error must reject by hand.
                try {
                    const [lat, lon] = grid.snapToTwoKmCentre(position.coords.latitude, position.coords.longitude);
                    const accuracy = position.coords.accuracy;
                    resolve({
                        lat,
                        lon,
                        accuracy: Number.isFinite(accuracy) ? Math.round(accuracy) : null,
                    });
                } catch (error) {
                    reject(error);
                }
            },
            reject,
            { timeout, maximumAge, enableHighAccuracy: false }
        );
    });
}
